import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

type ConsentCategories = {
  necessary: true;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
};

type StoredConsent = ConsentCategories & { timestamp: number };

const LOCAL_STORAGE_KEY = 'cc-consent-v1';
const COOKIE_NAME = 'cc_consent';

function readStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (typeof parsed === 'object' && parsed) return parsed;
  } catch {}
  return null;
}

function writeConsent(consent: StoredConsent) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(consent));
  } catch {}
  try {
    const value = encodeURIComponent(JSON.stringify(consent));
    document.cookie = `${COOKIE_NAME}=${value}; Max-Age=31536000; Path=/; SameSite=Lax; Secure`;
  } catch {}
  (window as any).__cookieConsent = consent;
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consent }));
}

const CookieConsent: React.FC = () => {
  const initial = useMemo<StoredConsent | null>(() => readStoredConsent(), []);
  const [showBanner, setShowBanner] = useState(!initial);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState<ConsentCategories>(
    () => initial ?? { necessary: true, preferences: false, analytics: false, marketing: false },
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    (window as any).__cookieConsent = initial;
    const handler = () => setSettingsOpen(true);
    window.addEventListener('openCookieSettings', handler as EventListener);
    return () => window.removeEventListener('openCookieSettings', handler as EventListener);
  }, [initial]);

  const acceptAll = () => {
    const consent: StoredConsent = {
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    };
    writeConsent(consent);
    setDraft(consent);
    setShowBanner(false);
  };

  const rejectNonEssential = () => {
    const consent: StoredConsent = {
      necessary: true,
      preferences: false,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    };
    writeConsent(consent);
    setDraft(consent);
    setShowBanner(false);
  };

  const openCustomize = () => setSettingsOpen(true);
  const closeCustomize = () => setSettingsOpen(false);

  const saveCustomize = () => {
    const consent: StoredConsent = { ...draft, necessary: true, timestamp: Date.now() };
    writeConsent(consent);
    setShowBanner(false);
    setSettingsOpen(false);
  };

  return (
    <>
      {showBanner &&
        (isMobile ? (
          <Dialog open={showBanner} fullWidth maxWidth="xs">
            <DialogTitle>We value your privacy</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                We use necessary cookies to make our site work. With your consent, we also use
                cookies for preferences, analytics, and marketing as described in our Privacy
                Policy. You can change your choices anytime in Cookie Settings.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2 }}>
              <Button variant="contained" onClick={acceptAll} sx={{ mb: 1 }}>
                Accept all
              </Button>
              <Button variant="outlined" onClick={rejectNonEssential} sx={{ mb: 1 }}>
                Reject non-essential
              </Button>
              <Button onClick={openCustomize}>Customize</Button>
            </DialogActions>
          </Dialog>
        ) : (
          <Box
            sx={{
              position: 'fixed',
              bottom: 16,
              left: 16,
              right: 16,
              zIndex: (t) => t.zIndex.snackbar,
            }}
          >
            <Paper elevation={6} sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ flex: 1, pr: { sm: 2 } }}>
                  <Typography variant="subtitle1" gutterBottom>
                    We value your privacy
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We use necessary cookies to make our site work. With your consent, we also use
                    cookies for preferences, analytics, and marketing as described in our Privacy
                    Policy. You can change your choices anytime in Cookie Settings.
                  </Typography>
                </Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ minWidth: { sm: 280 } }}
                >
                  <Button variant="outlined" onClick={openCustomize}>
                    Customize
                  </Button>
                  <Button variant="outlined" onClick={rejectNonEssential}>
                    Reject non-essential
                  </Button>
                  <Button variant="contained" onClick={acceptAll}>
                    Accept all
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        ))}

      <Dialog open={settingsOpen} onClose={closeCustomize} fullWidth maxWidth="sm">
        <DialogTitle>Cookie Settings</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Strictly necessary</Typography>
            <Typography variant="body2" color="text.secondary">
              Always on. Required for core functionality (security, network management,
              accessibility).
            </Typography>
          </Box>
          <FormControlLabel control={<Switch checked disabled />} label="Necessary" />
          <FormControlLabel
            control={
              <Switch
                checked={draft.preferences}
                onChange={(e) => setDraft((d) => ({ ...d, preferences: e.target.checked }))}
              />
            }
            label="Preferences"
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.analytics}
                onChange={(e) => setDraft((d) => ({ ...d, analytics: e.target.checked }))}
              />
            }
            label="Analytics"
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.marketing}
                onChange={(e) => setDraft((d) => ({ ...d, marketing: e.target.checked }))}
              />
            }
            label="Marketing"
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Your choices will be stored for one year. You can revisit these settings from the
              footer at any time.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCustomize}>Cancel</Button>
          <Button onClick={rejectNonEssential}>Reject non-essential</Button>
          <Button variant="contained" onClick={saveCustomize}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
